import { Backdrop, CircularProgress, createTheme, Grid, IconButton, MuiThemeProvider, Typography } from '@material-ui/core';
import { Add as AddIcon, Delete as DeleteIcon } from '@material-ui/icons';
import MUIDataTable from 'mui-datatables';
import { useSnackbar } from 'notistack';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Select from '../../../components/Form/Select';
import TextField from '../../../components/Form/TextField';
import AlertDialog from '../../../components/ui/AlertDialog';
import ButtonUI from '../../../components/ui/ButtonUI';
import { useAuth } from '../../../hooks/auth';
import { MvnOutgoingMaterialInvoicePost } from '../../../interfaces/Mvm';
import MvmApi from '../../../services/MvmApi';
import getDefaultGridOptions, { defaultHeaderOptions } from '../../../utils/componentUtils';
import { OUTGOING_MATERIAL_INVOICE_TYPE } from '../../../utils/constants';
import { useStyles } from '../Style';

type props =  {
    mvmStatus: string,
    mvmId: number,
    outgoingMaterialInvoices: any[],
    setOutgoingMaterialInvoices: any
};

interface IFormInput {
    invoiceKey: string;
    outgoingRequestType: string;
}

//allowInvoiceEdit: boolean - Indica se deve exibir a coluna para editar NF de saida
//showColumns: array - define quais colunas adicionais devem ser exibidas na grid.
const MvmOutgoingMaterialInvoiceList: React.FC<props> = ({ mvmId, outgoingMaterialInvoices, setOutgoingMaterialInvoices, mvmStatus }) => {
    useAuth();
    const classes = useStyles();
    const { t } = useTranslation("Mvm");
    const { t: _t } = useTranslation("General");
    const [circularLoading, setCircularLoading] = useState(false)
    const { enqueueSnackbar } = useSnackbar()
    const { control, handleSubmit, setValue, getValues } = useForm<IFormInput>({
        defaultValues: {
            invoiceKey: '',
            outgoingRequestType: '',
        }
    })
    const refFieldInvoiceKey = useRef<HTMLInputElement>(null)

    const myTheme = createTheme({
        overrides: {
            MuiTableBody: {
                root: {
                    cursor: ''
                }
            }
        }
    })

    useEffect(() => {
        searchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const isValidStatus = () : boolean => {
        const invalidStatus = ["PEM", "CON", "CAN"];

        return invalidStatus.includes(mvmStatus);
    }

    const columns = [
        {
            name: 'id',
            options: {
                display: false
            }
        },
        {
            name: 'outgoingRequestType',
            label: t("invoiceType"),
            options: {
                ...defaultHeaderOptions,
                sort: false,
                customBodyRender: (value: any, tableMeta: any) => (
                    OUTGOING_MATERIAL_INVOICE_TYPE.find((x: any) => x.code === value)?.label
                )
            }
        },
        {
            name: 'invoiceKey',
            label: t("invoiceKey"),
            options: {
                ...defaultHeaderOptions,
                sort: false
            }
        },
        {
            name: 'invoiceNumber',
            label: t("invoiceNumber"),
            options: {
                ...defaultHeaderOptions,
                sort: false
            }
        },   
        {
            name: 'id',
            label: ' ',
            options: {
                setCellProps: () => ({
                    align: "right"
                }),
                disableClick: true,
                sort: false,
                customBodyRender: (value: any, tableMeta: any) => (
                    renderButtons(tableMeta)
                )
            }
        }        
    ]

    const renderButtons = (item: any) => {
        return (
            <AlertDialog
                dialogTitle={_t("dialogDeleteTitle")}
                dialogContentText={_t("dialogDeleteContentText")}
                cancelTextButton={_t("cancelDeleteTextButton")}
                confirmTextButton={_t("confirmDeleteTextButton")}
                confirmDialogFunction={() => { deleteInvoice(item.rowData, item.rowIndex) }}
                Button={
                    <IconButton size='small' edge="end">
                        <DeleteIcon />
                    </IconButton>
                }
            />
        );
    }

    const insertInvoice = async (data: any) => {
        if (data.outgoingRequestType === "" || !data.outgoingRequestType) {
            enqueueSnackbar(t("emptyInvoiceType"), { variant: 'warning' });
            return;
        }

        if (data.invoiceKey === "") {
            enqueueSnackbar(t("emptyInvoiceKey"), { variant: 'warning' });
            return;
        }

        if (data.invoiceKey.length !== 44) {
            enqueueSnackbar(t("invoiceKeyNot44CharValidation"), { variant: 'warning' });
            return;
        }

        if (outgoingMaterialInvoices.find((x: any) => x.invoiceKey === data.invoiceKey)){
            enqueueSnackbar(t("alreadyInsertedInvoice"), { variant: 'warning' });
            return;
        }

        const request: MvnOutgoingMaterialInvoicePost = {
            mvmId: mvmId,
            invoiceKey: data.invoiceKey,
            invoiceNumber: String(Number(data.invoiceKey?.substring(25,34))),
            outgoingRequestType: data.outgoingRequestType.code
        }

        const newInvoicestoAdd = [...outgoingMaterialInvoices, request];

        setOutgoingMaterialInvoices(newInvoicestoAdd);

        setValue("invoiceKey", "");
        setValue("outgoingRequestType", "");

        return;
    }

    const searchInvoices = async () => {
        setCircularLoading(true);

        await MvmApi.searchOutgoingMaterialInvoice(mvmId)
            .then((res: any) => {
                setOutgoingMaterialInvoices(res);
            })
            .catch((res: any) => {
                enqueueSnackbar(res.message, { variant: 'error' });
            })
            .finally(() => {
                setCircularLoading(false);
            })
    }

    const deleteInvoice = async (rowData: any, rowIndex: number) => {
        const id: number = rowData[0];
        
        if (id > 0) {
            setCircularLoading(true);
            await MvmApi.deleteOutgoingMaterialInvoice(id)
                .then(() => {
                    enqueueSnackbar(_t("returnSubmitDeleteFormSuccess"), { variant: 'success' });
                })
                .catch((res: any) => {
                    enqueueSnackbar(res.message, { variant: 'error' });
                })
                .finally(() => {
                    setCircularLoading(false);
                })
        }

        const newInvoicesArr = outgoingMaterialInvoices.filter((invoice: any) => invoice.invoiceKey !== rowData[2]);
        setOutgoingMaterialInvoices(newInvoicesArr);
    }

    const onSubmit = (data: any) => {}
    
    return (
        <Grid style={{ marginTop: 20, width: "98%", marginLeft: 10, marginBottom: 10 }}>
            <Typography className={classes.root} style={{ marginLeft: 0, marginBottom: 10 }} >
                {t("outgoingInvoiceMaterial")}
            </Typography>

            <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={1}>
                    <Grid item xs={2} >
                        <Select
                            control={control}
                            label={t('invoiceType')} 
                            name={'outgoingRequestType'}
                            options={OUTGOING_MATERIAL_INVOICE_TYPE}
                            disabled={isValidStatus()}
                            allowEnterKey={false} />
                    </Grid>

                    <Grid item xs={2} >
                        <TextField
                            control={control} 
                            type="text"
                            fullWidth
                            name="invoiceKey"
                            disable={isValidStatus()}
                            inputProps={{maxLength: 44}}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    insertInvoice(getValues())
                                    refFieldInvoiceKey.current?.focus()
                                }
                            }}
                            onInput={(e) => { 
                                (e.target as HTMLInputElement).value = (e.target as HTMLInputElement).value.slice(0, 44);
                                (e.target as HTMLInputElement).value = (e.target as HTMLInputElement).value.replace(/[^0-9.]/g, '').replace(/(\..*?)\..*/g, '$1');
                            }}
                            label={t("invoiceKey")}
                            inputRef={refFieldInvoiceKey} 
                        />
                    </Grid>
                    <ButtonUI
                            color="primary"
                            onClick={() => insertInvoice(getValues())}
                            disabled={isValidStatus()}
                            variant="contained"
                            style={{ 
                                    maxWidth: '40px', 
                                    maxHeight: '38px', 
                                    minWidth: '40px', 
                                    minHeight: '38px', 
                                    marginTop: 22, 
                                    marginLeft: '15px' }}>
                            <AddIcon />
                        </ButtonUI>
                </Grid>
            </form>

            <Backdrop style={{ zIndex: 9999 }} open={circularLoading}>
                <CircularProgress color="inherit" />
            </Backdrop>

            <MuiThemeProvider theme={myTheme}>
                <MUIDataTable
                    title=""
                    data={outgoingMaterialInvoices}
                    columns={columns}
                    options={{
                        ...getDefaultGridOptions(_t),
                        serverSide: false,
                        pagination: false,
                        sort: false,
                        selectToolbarPlacement: "none",
                        filter: false,
                        search: false,
                        download: false,
                        print: false,
                        viewColumns: false,
                        selectableRows: 'none',
                    }}
                />
            </MuiThemeProvider>
        </Grid>
    )
}

export default MvmOutgoingMaterialInvoiceList